import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { matchApi, matchPlayerApi } from '../../services/api';
import MatchAutocomplete from '../../components/Cricket/MatchAutocomplete';
import GeminiModelSelect from '../../components/GeminiModelSelect';
import { type Match } from '../../models';

export interface PlayerUpdate {
  id: number;
  lineup_status: string;
  prev_bat_pos: number | null;
  name?: string;
  old_status?: string;
  old_bat_pos?: number | null;
}

const statusOptions = [
  { value: 'PLAYING', label: 'Playing' },
  { value: 'NOT_PLAYING', label: 'Not Playing' },
  { value: 'LAST_PLAYED', label: 'Last Played' },
  { value: 'UNAVAILABLE', label: 'Unavailable' },
  { value: 'X_FACTOR_SUBSTITUTE', label: 'X-Factor Substitute' },
  { value: 'USED_X_FACTOR_SUBSTITUTE', label: 'Used X-Factor Substitute' },
  { value: 'CAN_PLAY', label: 'Can Play' },
];

const AILineupPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialMatchId = searchParams.get('match') ? Number(searchParams.get('match')) : null;

  const [match, setMatch] = useState<Match | null>(null);
  const [lineupText, setLineupText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedLlmModel, setSelectedLlmModel] = useState('');

  // AI Response States
  const [parsedUpdates, setParsedUpdates] = useState<PlayerUpdate[] | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);

  // Global Target Status Maps
  const [playingTargetStatus, setPlayingTargetStatus] = useState('PLAYING');
  const [subTargetStatus, setSubTargetStatus] = useState('X_FACTOR_SUBSTITUTE');
  const [notPlayingTargetStatus, setNotPlayingTargetStatus] = useState('NOT_PLAYING');
  const [unavailableMapping, setUnavailableMapping] = useState('KEEP_UNAVAILABLE');

  useEffect(() => {
    if (initialMatchId && !match) {
      matchApi.getById(initialMatchId).then((res) => setMatch(res.data)).catch(console.error);
    }
  }, [initialMatchId, match]);

  const processLLMResponse = (updates: any[], players: any[], is_probable: boolean) => {
    setIsConfirmed(!is_probable);
    const enrichedUpdates = updates.map((update: any) => {
      const local = players.find((p: any) => p.id === update.id);
      
      let targetStatus = update.lineup_status;

      if (targetStatus === 'PLAYING') {
        targetStatus = playingTargetStatus;
      } else if (targetStatus === 'X_FACTOR_SUBSTITUTE') {
        targetStatus = subTargetStatus;
      } else if (targetStatus === 'NOT_PLAYING') {
        if (local?.lineup_status === 'UNAVAILABLE' && unavailableMapping === 'KEEP_UNAVAILABLE') {
          targetStatus = 'UNAVAILABLE';
        } else if (notPlayingTargetStatus === 'IGNORE') {
          targetStatus = local?.lineup_status || 'NOT_PLAYING';
        } else {
          targetStatus = 'NOT_PLAYING';
        }
      }

      return {
        ...update,
        lineup_status: targetStatus,
        name: local?.player?.name || `Unknown (ID: ${update.id})`,
        old_status: local?.lineup_status,
        old_bat_pos: local?.prev_bat_pos,
      };
    });
    setParsedUpdates(enrichedUpdates);
  };

  const handlePredictLineup = async () => {
    if (!match) return;
    setLoading(true);
    try {
      const playerRes = await matchPlayerApi.getByMatch(match.id);
      const players = playerRes.data?.results || [];

      const response = await matchApi.aiPredictLineup(match.id, selectedLlmModel || undefined);
      const { is_probable, updates } = response.data;
      processLLMResponse(updates, players, is_probable);
    } catch (error) {
      console.error('Error predicting lineup:', error);
      alert('Failed to predict lineup using Web Search AI.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!lineupText.trim() || !match) return;
    setLoading(true);
    try {
      const playerRes = await matchPlayerApi.getByMatch(match.id);
      const players = playerRes.data?.results || [];

      const response = await matchApi.aiLineup(match.id, { lineup_text: lineupText, llm_model: selectedLlmModel || undefined });
      const { is_probable, updates } = response.data;
      processLLMResponse(updates, players, is_probable);
    } catch (error) {
      console.error('Error parsing lineup:', error);
      alert('Failed to parse lineup using AI.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChange = (id: number, field: keyof PlayerUpdate, value: any) => {
    if (!parsedUpdates) return;
    const newUpdates = parsedUpdates.map((u) => {
      if (u.id === id) {
        return { ...u, [field]: value };
      }
      return u;
    });
    setParsedUpdates(newUpdates);
  };

  const handleApply = async () => {
    if (!parsedUpdates || !match) return;
    setSaving(true);
    try {
      await matchPlayerApi.bulkUpdate({
        match_id: match.id,
        lineup_announced: isConfirmed,
        updates: parsedUpdates,
      });
      alert('Lineup successfully updated and saved!');
      navigate(`/matches/${match.id}/view`);
    } catch (error) {
      console.error('Save failed:', error);
      alert('Failed to apply updates.');
    } finally {
      setSaving(false);
    }
  };

  const handlePlayingTargetChange = (e: any) => {
    setPlayingTargetStatus(e.target.value);
  };

  const getSummaryCounts = () => {
    if (!parsedUpdates) return { playing: 0, notPlaying: 0, substitute: 0 };
    let playing = 0, notPlaying = 0, substitute = 0;
    parsedUpdates.forEach((u) => {
      if (['PLAYING', 'LAST_PLAYED', 'CAN_PLAY'].includes(u.lineup_status)) playing++;
      else if (['X_FACTOR_SUBSTITUTE', 'USED_X_FACTOR_SUBSTITUTE'].includes(u.lineup_status)) substitute++;
      else notPlaying++;
    });
    return { playing, notPlaying, substitute };
  };

  const summary = getSummaryCounts();

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <Typography variant="h4" mb={3}>
        AI Lineup Builder
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" mb={2}>1. Select Target Match</Typography>
            <MatchAutocomplete
              value={match}
              onChange={(value) => setMatch(value)}
            />
            {match && (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Current Lineup Status:</strong> {match.lineup_announced ? "Announced" : "Not Announced"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Status:</strong> {match.match_status}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  <strong>Global Parse Mappings:</strong> Automatically map AI results to required statuses.
                </Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Playing Mapping</Typography>
                    <FormControl size="small" fullWidth>
                      <Select value={playingTargetStatus} onChange={handlePlayingTargetChange}>
                        <MenuItem value="PLAYING">PLAYING</MenuItem>
                        <MenuItem value="CAN_PLAY">CAN_PLAY</MenuItem>
                        <MenuItem value="LAST_PLAYED">LAST_PLAYED</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Substitute Mapping</Typography>
                    <FormControl size="small" fullWidth>
                      <Select value={subTargetStatus} onChange={(e) => setSubTargetStatus(e.target.value)}>
                        <MenuItem value="X_FACTOR_SUBSTITUTE">X_FACTOR_SUBSTITUTE</MenuItem>
                        <MenuItem value="USED_X_FACTOR_SUBSTITUTE">USED_X_FACTOR_SUBSTITUTE</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Not Playing Mapping</Typography>
                    <FormControl size="small" fullWidth>
                      <Select value={notPlayingTargetStatus} onChange={(e) => setNotPlayingTargetStatus(e.target.value)}>
                        <MenuItem value="NOT_PLAYING">NOT_PLAYING</MenuItem>
                        <MenuItem value="IGNORE">IGNORE (Keep original status)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Unavailable Players</Typography>
                    <FormControl size="small" fullWidth>
                      <Select value={unavailableMapping} onChange={(e) => setUnavailableMapping(e.target.value)}>
                        <MenuItem value="KEEP_UNAVAILABLE">KEEP_UNAVAILABLE (Default)</MenuItem>
                        <MenuItem value="MAP_TO_NOT_PLAYING">OVERRIDE (Trust LLM)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box mt={3}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Gemini Model (applies to both actions below)
              </Typography>
              <GeminiModelSelect value={selectedLlmModel} onChange={setSelectedLlmModel} fullWidth />
            </Box>

            <Box mt={3} mb={3}>
              <Typography variant="h6" mb={1}>2A. Predictive Web Search</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Automatically query the internet for standard expected lineup coverage without manually providing a squad text.
              </Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                fullWidth 
                size="large" 
                onClick={handlePredictLineup} 
                disabled={loading || !match}
              >
                {loading ? <CircularProgress size={24} /> : 'Search Internet & Predict Probable Lineup'}
              </Button>
            </Box>

            <Typography variant="h6" mt={4} mb={2}>2B. Paste Lineup Data</Typography>
            <TextField
              label="Raw Squad Text"
              multiline
              rows={12}
              fullWidth
              value={lineupText}
              onChange={(e) => setLineupText(e.target.value)}
              disabled={loading || !match}
              placeholder="E.g. Squad playing today: Labuschagne, Warner..."
            />
            
            <Button 
              variant="contained" 
              color="primary" 
              fullWidth 
              size="large" 
              sx={{ mt: 2 }} 
              onClick={handleAnalyze} 
              disabled={loading || !match || !lineupText.trim()}
            >
              {loading ? <CircularProgress size={24} /> : 'Process Lineup via AI'}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, minHeight: '600px' }}>
            <Typography variant="h6" mb={2}>3. Review & Apply Predictions</Typography>
            
            {!parsedUpdates ? (
              <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">Awaiting processing...</Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip label={`Playing: ${summary.playing}`} color="success" />
                    <Chip label={`Subs: ${summary.substitute}`} color="warning" />
                    <Chip label={`Not Playing: ${summary.notPlaying}`} color="default" />
                  </Box>
                  <FormControlLabel
                    control={<Switch checked={isConfirmed} onChange={(e) => setIsConfirmed(e.target.checked)} color="primary" />}
                    label={<strong>Confirmed Lineup (Updates Match State)</strong>}
                  />
                  <Button variant="contained" color="secondary" onClick={handleApply} disabled={saving}>
                    {saving ? 'Saving...' : 'Save All Updates to DB'}
                  </Button>
                </Box>
                
                <Box sx={{ maxHeight: '600px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #ccc' }}>
                        <th style={{ padding: '8px' }}>Name</th>
                        <th style={{ padding: '8px' }}>Status Transition</th>
                        <th style={{ padding: '8px', width: '200px' }}>Batting Pos Setup</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedUpdates.map((update) => (
                        <tr key={update.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px 8px' }}>
                            <Typography variant="body2" fontWeight="bold">{update.name}</Typography>
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                                {update.old_status || 'UNKNOWN'}
                              </Typography>
                              <ArrowRightAltIcon fontSize="small" color="action" />
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                  value={update.lineup_status || ''}
                                  onChange={(e) => handleUpdateChange(update.id, 'lineup_status', e.target.value)}
                                  sx={{ 
                                    height: '30px', 
                                    backgroundColor: ['PLAYING', 'CAN_PLAY', 'LAST_PLAYED'].includes(update.lineup_status) ? 'rgba(76, 175, 80, 0.1)' : 'inherit'
                                  }}
                                >
                                  {statusOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {update.old_bat_pos || '-'}
                              </Typography>
                              <ArrowRightAltIcon fontSize="small" color="action" />
                              <TextField
                                type="number"
                                size="small"
                                value={update.prev_bat_pos === null ? '' : update.prev_bat_pos}
                                onChange={(e) => handleUpdateChange(update.id, 'prev_bat_pos', e.target.value ? parseInt(e.target.value) : null)}
                                sx={{ width: 70 }}
                                inputProps={{ style: { padding: '4.5px 14px' } }}
                              />
                            </Box>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AILineupPage;
